import * as React from 'react';
import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';

import { ErrorAlert, IErrorInfo } from './ErrorAlert';

import * as Web from "./Web";

interface PasswordControlProps {
   label?: string
   password?: string
   setPassword: (e: string) => void
}

const PasswordControl = ({ label, password, setPassword }: PasswordControlProps) => {
   const [showPassword, setShowPassword] = React.useState(false);

   const onShowPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setShowPassword((show) => !show);
   }

   const onMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
   };

   return (
      <FormControl sx={{ width: '40ch', mb: 10 }} variant="outlined">
         <InputLabel htmlFor={label}>{label}</InputLabel>
         <OutlinedInput
            id={label}
            type={showPassword ? 'text' : 'password'}
            value={password}
            label={label}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            endAdornment={
               <InputAdornment position="end">
                  <IconButton
                     aria-label="toggle password visibility"
                     edge="end"
                     onClick={onShowPassword}
                     onMouseDown={onMouseDownPassword}
                  >
                     {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
               </InputAdornment>
            }
         />
      </FormControl>);
}

export const PasswordPage = () => {
   const [userName, setUserName] = React.useState("");
   const [password, setPassword] = React.useState("");
   const [newPassword, setNewPassword] = React.useState("");
   const [confirmPassword, setConfirmPassword] = React.useState("");
   const [changePassword, setChangePassword] = React.useState(false);
   const [passwordMismatch, setPasswordMismatch] = React.useState(false);
   const [passwordLengthError, setPasswordLengthError] = React.useState(false);
   const [submitDisbled, setSubmitDisbled] = React.useState(true);
   const [error, setError] = React.useState<IErrorInfo | null>(null); 
   const query = new URLSearchParams(useLocation().search);
   const changeRequired = query.get("change");
   const oauthState = query.get("state");

   React.useEffect(() => {
      setChangePassword(changeRequired ? true : false);
   }, [changeRequired]);

   const keyPressOverride = React.useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
       if (e.key === "Enter") e.preventDefault();
   }, []);

   React.useEffect(() => {
      setSubmitDisbled((passwordMismatch || !password || (changePassword && !newPassword)) ? true : false);
   }, [passwordMismatch, changePassword, password, newPassword]);

   React.useEffect(() => {
      setPasswordLengthError(!newPassword || newPassword?.length < 8);
      setPasswordMismatch(((newPassword || confirmPassword) && newPassword !== confirmPassword) ? true: false);
   }, [newPassword, confirmPassword]);

   const onSubmit = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (passwordMismatch) {
         return;
      }
      let controller = new AbortController();
      fetch();
      async function fetch() {
         const params = { userName, password, newPassword, state: oauthState };
         const response = await Web.httpPost('/oauth/login/', params, controller);
         if (response?.failed) {
            setError(response);
         }
         else {
            setChangePassword(response?.result?.changePasswordRequired);
            setNewPassword("");
            setConfirmPassword("");
            setError(response);
         }
      }
      return function cleanup() {
         controller.abort();
      };
   }, [passwordMismatch, userName, password, newPassword, oauthState]);

   return (
      <Container sx={{ mt: 10 }} >
         {(error) ? <ErrorAlert {...error} /> : undefined}
         <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                   <Typography variant="h4">{"IOP Workshop"}</Typography>
                   <Typography variant="body1">{"Enter email and password used for offline mode."}</Typography>
                   <Typography variant="body1">{"The workshop administrator sets the default password."}</Typography>
            </Box>
            <form action='oauth/login' onSubmit={onSubmit} onKeyUp={keyPressOverride}>
               <Card>
                  <CardContent sx={{ p: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                     <FormControl sx={{ width: '40ch', mb: 10 }} variant="outlined">
                        <InputLabel htmlFor="username">{"Email Address"}</InputLabel>
                        <OutlinedInput
                            id="username"
                            type="text"
                            label={"Email Address"}
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                        />
                     </FormControl>
                           <PasswordControl label={`${"Password"}`} password={password} setPassword={setPassword} />
                     {(changePassword) ?
                               <PasswordControl label={`${"New Password"}`} password={newPassword} setPassword={setNewPassword} /> :
                        null
                     }
                     {(changePassword && passwordLengthError) ?
                               <Typography variant="error" sx={{ mb: 4 }}>{"Password is too short."}</Typography> :
                        null
                     }
                     {(changePassword) ?
                               <PasswordControl label={`${"Confirm Password"}`} password={confirmPassword} setPassword={setConfirmPassword} /> :
                        null
                     }
                     {(changePassword && passwordMismatch) ?
                               <Typography variant="error" sx={{ mb: 4 }}>{"Passwords do not match."}</Typography> :
                        null
                     }
                     <Button
                        variant="contained"
                        type="submit"
                        disabled={submitDisbled}
                     >
                        {"Submit"}
                     </Button>
                  </CardContent>
               </Card>
            </form>
         </Box>

      </Container >
   );
}

export default PasswordPage;
